import type { AlertDialog } from '@base-ui/react';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomCondition } from '~/api/conditions/types';

import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';

import { CustomConditionMobileCell } from './CustomConditionsTableCells/CustomConditionMobileCell';
import { CustomConditionNameCell } from './CustomConditionsTableCells/CustomConditionNameCell';
import { CustomConditionOptionsCell } from './CustomConditionsTableCells/CustomConditionOptionsCell';

export type DialogHandles = Record<'deleteCustomConditionHandle', AlertDialog.Handle<CustomCondition>>;

const columnHelper = createAppColumnHelper<CustomCondition>();

export const useCustomConditionsTableColumns = ({ dialogHandles }: { dialogHandles: DialogHandles }) => {
  const { t } = useTranslation(['settings']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Name
        columnHelper.accessor('name', {
          id: ColumnIds.customConditionsName,
          header: t('settings:general.customConditions.table.name'),
          cell: ({ getValue }) => <CustomConditionNameCell name={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: customCondition } }) => (
            <CustomConditionOptionsCell customCondition={customCondition} dialogHandles={dialogHandles} />
          ),
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.customConditionsMobile,
          cell: ({ row: { original: customCondition } }) => (
            <CustomConditionMobileCell customCondition={customCondition} dialogHandles={dialogHandles} />
          ),
          meta: { mobile: true },
        }),
      ]),
    [t, dialogHandles],
  );
};
